import { Controller } from "stimulus";

const LIVE_STATUS = "live";
const PENDING_STATUS = "pending";
const IDLE_STATUS = "idle";
const ERROR_STATUS = "error";
const RECORDER_MIME_TYPE = "audio/webm;codecs=opus";
const FINALIZATION_TIMEOUT_MS = 4000;

export default class extends Controller {
  static targets = [
    "startButton",
    "stopButton",
    "translationToggle",
    "languageSelect",
    "translationPanel",
    "translationTitle",
    "status",
    "error",
    "sourceOutput",
    "sourceEmpty",
    "translationOutput",
    "translationEmpty"
  ];

  static values = {
    sessionUrl: String,
    reconnectUrl: String,
    chunkMs: { type: Number, default: 250 }
  };

  connect() {
    this.resetState();
    this.syncTranslationControls();
    this.renderSourceTranscript();
    this.renderTranslationTranscript();
  }

  disconnect() {
    this.teardown();
  }

  toggleTranslation() {
    this.syncTranslationControls();
    this.renderTranslationTranscript();
  }

  async start(event) {
    event?.preventDefault();
    if (this.isRunning) {
      return;
    }

    if (!this.browserIsSupported()) {
      this.setError("Ce navigateur ne supporte pas la capture micro requise. Pour ce test, utilisez Chrome ou Edge recents.");
      return;
    }

    if (!window.MediaRecorder.isTypeSupported(RECORDER_MIME_TYPE)) {
      this.setError("Le format micro requis par DeepL n'est pas supporte ici. Pour ce test, utilisez Chrome ou Edge.");
      return;
    }

    this.clearMessages();
    this.resetTranscriptBuffers();
    this.syncTranslationControls();
    this.renderSourceTranscript();
    this.renderTranslationTranscript();
    this.setStatus("Autorisation du microphone...", PENDING_STATUS);

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const session = await this.postJson(this.sessionUrlValue, this.buildSessionPayload());
      this.currentToken = session.token;
      this.streamingUrl = session.streaming_url;
      this.sessionId = session.session_id;
      this.intentionalStop = false;
      this.endOfSourceSent = false;
      this.reconnectAttempts = 0;
      this.receivedTerminalEvent = false;
      this.pendingChunks = [];

      this.setStatus("Connexion a DeepL...", PENDING_STATUS);
      await this.openSocket(session.streaming_url, session.token);
      this.startRecorder();

      this.isRunning = true;
      this.toggleControls(true);
      this.setStatus("Sous-titres en direct actifs.", LIVE_STATUS);
    } catch (error) {
      this.setError(error.message || "Impossible de lancer les sous-titres.");
      this.teardown();
    }
  }

  async stop(event) {
    event?.preventDefault();
    await this.stopStreaming();
  }

  buildSessionPayload() {
    const payload = {
      source_language_mode: "auto",
      target_languages: []
    };

    if (this.translationEnabled && this.languageSelectTarget.value) {
      payload.target_languages = [this.languageSelectTarget.value];
    }

    return payload;
  }

  browserIsSupported() {
    return Boolean(
      navigator.mediaDevices?.getUserMedia &&
      window.MediaRecorder &&
      window.WebSocket &&
      window.Blob
    );
  }

  get translationEnabled() {
    return this.translationToggleTarget.checked;
  }

  syncTranslationControls() {
    const enabled = this.translationEnabled;
    this.languageSelectTarget.disabled = !enabled || this.isRunning;
    this.translationPanelTarget.hidden = !enabled;
    if (enabled) {
      this.translationTitleTarget.textContent = `Traduction (${this.languageSelectTarget.selectedOptions[0]?.text || this.languageSelectTarget.value})`;
    }
  }

  toggleControls(running) {
    this.startButtonTarget.disabled = running;
    this.stopButtonTarget.disabled = !running;
    this.translationToggleTarget.disabled = running;
    this.languageSelectTarget.disabled = !this.translationEnabled || running;
  }

  clearMessages() {
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  setStatus(message, status) {
    this.statusTarget.textContent = message;
    this.statusTarget.dataset.state = status;
  }

  setError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
    this.setStatus("Sous-titres inactifs.", ERROR_STATUS);
  }

  resetState() {
    this.isRunning = false;
    this.intentionalStop = false;
    this.endOfSourceSent = false;
    this.receivedTerminalEvent = false;
    this.reconnectAttempts = 0;
    this.pendingChunks = [];
    this.mediaStream = null;
    this.mediaRecorder = null;
    this.socket = null;
    this.currentToken = null;
    this.streamingUrl = null;
    this.sessionId = null;
    this.finalizationResolver = null;
    this.resetTranscriptBuffers();
    this.toggleControls(false);
    this.setStatus("Sous-titres inactifs.", IDLE_STATUS);
  }

  resetTranscriptBuffers() {
    this.sourceTranscript = { finalText: "", tentativeText: "" };
    this.translationTranscript = { finalText: "", tentativeText: "", language: null };
  }

  async stopStreaming() {
    if (!this.isRunning && !this.socket && !this.mediaStream) {
      return;
    }

    this.intentionalStop = true;
    this.setStatus("Arret des sous-titres...", PENDING_STATUS);

    try {
      await this.stopRecorder();
      await this.sendEndOfSourceMedia();
      await this.waitForFinalization();
    } catch (error) {
      this.setError(error.message || "Erreur pendant l'arret des sous-titres.");
    } finally {
      this.teardown({ preserveTranscript: true });
      this.setStatus("Sous-titres arretes.", IDLE_STATUS);
    }
  }

  teardown({ preserveTranscript = true } = {}) {
    if (this.mediaRecorder) {
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onerror = null;
      this.mediaRecorder.onstop = null;
      if (this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
    }

    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }
    }

    this.mediaRecorder = null;
    this.mediaStream = null;
    this.socket = null;
    this.pendingChunks = [];
    this.isRunning = false;
    this.intentionalStop = false;
    this.endOfSourceSent = false;
    this.receivedTerminalEvent = false;
    this.reconnectAttempts = 0;
    this.currentToken = null;
    this.streamingUrl = null;
    this.sessionId = null;
    this.finalizationResolver = null;
    this.toggleControls(false);
    this.syncTranslationControls();

    if (!preserveTranscript) {
      this.resetTranscriptBuffers();
      this.renderSourceTranscript();
      this.renderTranslationTranscript();
    }
  }

  startRecorder() {
    this.mediaRecorder = new MediaRecorder(this.mediaStream, {
      mimeType: RECORDER_MIME_TYPE,
      audioBitsPerSecond: 32000
    });

    this.mediaRecorder.ondataavailable = async (event) => {
      if (!event.data || event.data.size === 0) {
        return;
      }

      try {
        const base64Data = await this.blobToBase64(event.data);
        this.enqueueOrSendChunk(base64Data);
      } catch (error) {
        this.setError("Impossible de traiter l'audio capture.");
      }
    };

    this.mediaRecorder.onerror = () => {
      this.setError("Erreur de capture micro.");
      this.stopStreaming();
    };

    this.mediaRecorder.start(this.chunkMsValue);
  }

  stopRecorder() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") {
        resolve();
        return;
      }

      this.mediaRecorder.onstop = () => resolve();
      this.mediaRecorder.stop();
    });
  }

  enqueueOrSendChunk(base64Data) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ source_media_chunk: { data: base64Data } }));
      return;
    }

    this.pendingChunks.push(base64Data);
    if (this.pendingChunks.length > 20) {
      this.pendingChunks.shift();
    }
  }

  flushPendingChunks() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.pendingChunks.forEach((chunk) => {
      this.socket.send(JSON.stringify({ source_media_chunk: { data: chunk } }));
    });
    this.pendingChunks = [];
  }

  async sendEndOfSourceMedia() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || this.endOfSourceSent) {
      return;
    }

    this.socket.send(JSON.stringify({ end_of_source_media: {} }));
    this.endOfSourceSent = true;
  }

  waitForFinalization() {
    if (this.receivedTerminalEvent || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.finalizationResolver = () => {
        resolve();
        this.finalizationResolver = null;
      };
      window.setTimeout(() => {
        resolve();
        this.finalizationResolver = null;
      }, FINALIZATION_TIMEOUT_MS);
    });
  }

  openSocket(streamingUrl, token) {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(`${streamingUrl}?token=${encodeURIComponent(token)}`);
      let resolved = false;

      socket.onopen = () => {
        resolved = true;
        this.socket = socket;
        this.flushPendingChunks();
        resolve();
      };

      socket.onmessage = (event) => {
        this.handleSocketMessage(event);
      };

      socket.onerror = () => {
        if (!resolved) {
          reject(new Error("Impossible d'ouvrir la connexion DeepL."));
        }
      };

      socket.onclose = () => {
        if (!resolved) {
          reject(new Error("La connexion DeepL s'est fermee avant d'etre prete."));
          return;
        }

        if (!this.intentionalStop && !this.receivedTerminalEvent) {
          this.handleUnexpectedClose();
        }
      };
    });
  }

  async handleUnexpectedClose() {
    if (this.reconnectAttempts >= 1 || !this.currentToken) {
      this.setError("Connexion DeepL interrompue. Relancez les sous-titres.");
      this.teardown({ preserveTranscript: true });
      return;
    }

    this.reconnectAttempts += 1;
    this.setStatus("Connexion interrompue, tentative de reconnexion...", PENDING_STATUS);

    try {
      const session = await this.postJson(this.reconnectUrlValue, { token: this.currentToken });
      this.currentToken = session.token;
      this.streamingUrl = session.streaming_url;
      await this.openSocket(session.streaming_url, session.token);
      this.setStatus("Connexion DeepL retablie.", LIVE_STATUS);
    } catch (error) {
      this.setError(error.message || "Echec de reconnexion a DeepL.");
      this.teardown({ preserveTranscript: true });
    }
  }

  handleSocketMessage(event) {
    const data = JSON.parse(event.data);

    if (data.source_transcript_update) {
      this.applyTranscriptUpdate(this.sourceTranscript, data.source_transcript_update);
      this.renderSourceTranscript();
      return;
    }

    if (data.target_transcript_update) {
      this.translationTranscript.language = data.target_transcript_update.language;
      this.applyTranscriptUpdate(this.translationTranscript, data.target_transcript_update);
      this.renderTranslationTranscript();
      return;
    }

    if (data.error) {
      this.receivedTerminalEvent = true;
      this.resolveFinalization();
      this.setError(data.error.error_message || "DeepL a retourne une erreur.");
      this.teardown({ preserveTranscript: true });
      return;
    }

    if (data.end_of_stream) {
      this.receivedTerminalEvent = true;
      this.resolveFinalization();
    }
  }

  applyTranscriptUpdate(buffer, update) {
    const concludedText = this.joinSegments(update.concluded || []);
    const tentativeText = this.joinSegments(update.tentative || []);

    if (concludedText) {
      buffer.finalText = this.concatTranscript(buffer.finalText, concludedText);
    }
    buffer.tentativeText = tentativeText;
  }

  joinSegments(segments) {
    return segments
      .map((segment) => (segment.text || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  concatTranscript(existingText, incomingText) {
    if (!incomingText) {
      return existingText;
    }
    if (!existingText) {
      return incomingText;
    }

    const needsSpace = !/[\\s'’-]$/.test(existingText) && !/^[,.;:!?)]/.test(incomingText);
    return needsSpace ? `${existingText} ${incomingText}` : `${existingText}${incomingText}`;
  }

  renderSourceTranscript() {
    this.sourceOutputTarget.innerHTML = this.renderTranscriptHtml(this.sourceTranscript);
    this.sourceEmptyTarget.hidden = Boolean(this.sourceTranscript.finalText || this.sourceTranscript.tentativeText);
  }

  renderTranslationTranscript() {
    if (!this.translationEnabled) {
      this.translationOutputTarget.innerHTML = "";
      this.translationEmptyTarget.hidden = true;
      return;
    }

    this.syncTranslationControls();
    this.translationOutputTarget.innerHTML = this.renderTranscriptHtml(this.translationTranscript);
    this.translationEmptyTarget.hidden = Boolean(this.translationTranscript.finalText || this.translationTranscript.tentativeText);
  }

  renderTranscriptHtml(transcript) {
    const finalText = this.escapeHtml(transcript.finalText || "");
    const tentativeText = this.escapeHtml(transcript.tentativeText || "");

    if (!finalText && !tentativeText) {
      return "";
    }

    return `
      <span>${finalText}</span>${tentativeText ? ` <span class="live-captions__tentative">${tentativeText}</span>` : ""}
    `.trim();
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  async blobToBase64(blob) {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";

    for (let index = 0; index < bytes.length; index += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
    }

    return window.btoa(binary);
  }

  async postJson(url, payload) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json"
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload)
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || `Erreur HTTP ${response.status}`);
    }

    return body;
  }

  resolveFinalization() {
    if (this.finalizationResolver) {
      this.finalizationResolver();
      this.finalizationResolver = null;
    }
  }
}
